import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Button, Modal } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../../components/auth/useAuth'
import {
    addBudget,
    getBudgets,
    editBudget,
    removeBudget,
    getNextBudgetNumber,
    updateAllAmpliations,
    duplicateBudget,
    convertToWO
} from '../../../actions/budgetActions'
import { getActiveBusinesses } from '../../../actions/businessesActions'
import { CustomDataTable } from '../../../components/design/Datatables/CustomDataTable'
import Swal from 'sweetalert2'
import Loader from '../../../components/loaders/Loader'
import BudgetForm from '../../../components/forms/BudgetForm'
import AmpliationForm from '../../../components/forms/AmpliationForm'

export default function Budget({ userType }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()

    const { budgets, numbudget, loading, resultbudget, total } = useSelector(state => state.budgets)
    const { businessesActives } = useSelector(state => state.businesses)

    const [modalShow, setModalShow] = useState(false)
    const [formState, setFormState] = useState({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isAddingOrEditing, setIsAddingOrEditing] = useState(false)

    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(25)
    const [search, setSearch] = useState('')
    const [searchAux, setSearchAux] = useState('')
    const [sortField, setSortField] = useState('date')
    const [sortOrder, setSortOrder] = useState('desc')

    const [activeTab, setActiveTab] = useState("form")

    const [ampliations, setAmpliations] = useState([])

    useEffect(() => {
        if (clientauth) {
            dispatch(getBudgets(page, limit, search, sortField, sortOrder))
            dispatch(getActiveBusinesses())
        }
    // eslint-disable-next-line
    }, [dispatch, clientauth, page, limit, search, sortField, sortOrder])

    useEffect(() => {
        if (numbudget) {
            setFormState(prev => ({ ...prev, numBudget: numbudget }))
        }
    }, [numbudget])

    const handleSort = (column, sortDirection) => {
        setSortField(column.id || 'date')
        setSortOrder(sortDirection)
        setPage(1)
    }

    const openModal = () => {
        setIsAddingOrEditing('adding')
        const initial = {
            numbudget: numbudget,
            date: new Date().toISOString().slice(0, 10),
            business: '',
            description: '',
            observations: '',
            conditions: '',
            validity: '',
            otherData: '',
            workLines: [],
            state: 'Pendiente',
            ampliations: []
        }
        setFormState(initial)
        setAmpliations([])
        dispatch(getNextBudgetNumber())
        setModalShow(true)
    }

    const selectBudget = (budget) => {
        if (budget.state === 'Pendiente') {
            setIsAddingOrEditing('editing')
        } else {
            setIsAddingOrEditing('viewing')
        }

        const normalized = {
            ...budget,
            id: budget._id,
            date: budget.date ? budget.date.split('T')[0] : '',
            business: budget.business?._id || '',
            description: budget.description || '',
            observations: budget.observations || '',
            conditions: budget.conditions || '',
            validity: budget.validity || '',
            otherData: budget.otherData || '',
            state: budget.state || 'Pendiente',
            workLines: Array.isArray(budget.workLines)
                ? budget.workLines.map(line => ({
                    typeLine: line.typeLine || '',
                    description: line.description || '',
                    params: line.params || [],
                    quantity: line.quantity || 0,
                    price: line.price || 0,
                    recurrence: line.recurrence || 'Sin recurrencia',
                    status: line.status || 'Pendiente',
                    amountRecurrence: line.amountRecurrence || 1
                }))
                : [],
            ampliations: Array.isArray(budget.ampliations)
                ? budget.ampliations.map(ampliation => ({
                    name: ampliation.name || '',
                    workLines: Array.isArray(ampliation.workLines)
                        ? ampliation.workLines.map(line => ({
                            typeLine: line.typeLine || '',
                            description: line.description || '',
                            params: line.params || [],
                            quantity: line.quantity || 0,
                            price: line.price || 0,
                            recurrence: line.recurrence || 'Sin recurrencia',
                            status: line.status || 'Pendiente',
                            amountRecurrence: line.amountRecurrence || 1
                        }))
                        : [],
                    state: ampliation.state || 'Pendiente',
                    date: ampliation.date ? ampliation.date.split('T')[0] : ''
                }))
                : []
        }

        setFormState(normalized)
        setAmpliations(normalized.ampliations || [])
        setModalShow(true)
    }

    const handleInputChange = ({ target }) => {
        setFormState(prev => ({
            ...prev,
            [target.name]: target.value
        }))
    }

    const handleSelectChange = ({ target }) => {
        setFormState(prev => ({
            ...prev,
            [target.name]: target.value
        }))
    }

    const handleSubmit = async (e, forcedStatus = null) => {
        e.preventDefault()
        setIsRegistering(true)

        const newFormState = {
            ...formState,
            ampliations,
            status: forcedStatus ?? formState.status
        }

        if (isAddingOrEditing === 'editing') {
            dispatch(editBudget(newFormState, newFormState._id))
        } else {
            dispatch(addBudget(newFormState))
        }

        setIsRegistering(false)
    }

    const handleDeleteBudget = (e, budgetId) => {
        e.preventDefault()
        Swal.fire({
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel'),
            icon: 'warning',
            html: t('admin.budgets.areyousuredeletebudget')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(removeBudget(budgetId))
            }
        })
    }

    const handleDuplicateBudget = (e, budgetId) => {
        e.preventDefault()

        Swal.fire({
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel'),
            icon: 'info',
            html: t('admin.budgets.areyousureduplicatebudget')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(duplicateBudget(budgetId))
            }
        })
    }

    const handleSubmitAmpliations = (updatedAmpliations) => {
        setAmpliations(updatedAmpliations);
        setFormState(prev => ({
            ...prev,
            ampliations: updatedAmpliations
        }));
        dispatch(updateAllAmpliations(updatedAmpliations, formState._id));
        setModalShow(false);
    };

    const handleConvertToWO = (e, budgetId, firstIndex, secondIndex) => {
        e.preventDefault()

        Swal.fire({
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel'),
            icon: 'info',
            html: t('admin.budgets.areyousureconverttoWO')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(convertToWO(budgetId, firstIndex, secondIndex))
            }
        })
    };

    useEffect(() => {
        if (resultbudget) {
            let resultbudgetTemp = resultbudget
            dispatch({ type: 'BUDGET_RESET_RESULT' })
            switch (resultbudgetTemp) {
                case 'addsuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.budgets.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing('adding')
                    })
                    break
                case 'editsuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.budgets.editsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break
                case 'deletesuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.budgets.deletesuccess')
                    }).then(() => {
                        dispatch(getBudgets(page, limit, search, sortField, sortOrder))
                    })
                    break
                case 'duplicated':
                    Swal.fire({
                        showConfirmButton: true,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.budgets.duplicated')
                    }).then(() => {
                        dispatch(getBudgets(page, limit, search, sortField, sortOrder))
                    })
                    break
                case 'convertsuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.budgets.convertsuccess')
                    }).then(() => {
                        dispatch(getBudgets(page, limit, search, sortField, sortOrder))
                        setModalShow(false)
                    })
                    break
                default:
                    break
            }
        }
        // eslint-disable-next-line
    }, [resultbudget])

    useEffect(() => {
        if (modalShow) {
            setActiveTab("form")
        }
    }, [modalShow])

    const columnsBudgets = [
        {
            id: 'date',
            name: t('admin.budgets.date'),
            grow: 0.5,
            cell: budget =>
                <div className='cursor' onClick={() => selectBudget(budget)}>
                    {new Date(budget.date).toLocaleDateString()}
                </div>,
            selector: budget => budget.date,
            sortable: true,
        },
        {
            id: 'numBudget',
            name: t('admin.budgets.numbudget'),
            grow: 0.5,
            cell: budget =>
                <div className='cursor' onClick={() => selectBudget(budget)}>
                    {budget.numBudget}
                </div>,
            selector: budget => budget.numBudget,
            sortable: true,
        },
        {
            id: 'business',
            name: t('admin.budgets.business'),
            grow: 3,
            cell: budget =>
                <div className='cursor text-start' onClick={() => selectBudget(budget)}>
                    {budget.business?.name || ''}
                </div>,
            selector: budget => budget.business?.name || '',
            sortable: true,
        },
        {
            id: 'total',
            name: t('admin.budgets.total'),
            grow: 1,
            right: true,
            cell: budget => {
                const baseTotal = (budget.workLines || [])
                    .reduce((sum, line) => sum + (line.price || 0), 0);

                const ampliationsTotal = (budget.ampliations || [])
                    .filter(amp => amp.state !== "Rechazado" && amp.state !== "Cancelado")
                    .flatMap(amp => amp.workLines || [])
                    .reduce((sum, line) => sum + (line.price || 0), 0);

                const total = (baseTotal + ampliationsTotal).toFixed(2);

                return (
                    <div className='cursor' onClick={() => selectBudget(budget)}>
                        {total} €
                    </div>
                );
            },
            selector: budget => {
                const baseTotal = (budget.workLines || [])
                    .reduce((sum, line) => sum + (line.price || 0), 0);

                const ampliationsTotal = (budget.ampliations || [])
                    .filter(amp => amp.state !== "Rechazado" && amp.state !== "Cancelado")
                    .flatMap(amp => amp.workLines || [])
                    .reduce((sum, line) => sum + (line.price || 0), 0);

                return baseTotal + ampliationsTotal;
            },
            sortable: true,
        },
        {
            id: 'workLines',
            name: t('admin.budgets.workLines'),
            grow: 1,
            center: true,
            cell: budget =>
                <div className='cursor' onClick={() => selectBudget(budget)}>
                    {(budget.workLines || []).length}
                </div>,
            selector: budget => (budget.workLines || []).length,
            sortable: true,
        },
        {
            name: t('admin.budgets.annexes'),
            grow: 1,
            center: true,
            cell: budget => {
                const validAmpliations = (budget.ampliations || [])
                    .filter(amp => amp.state !== "Rechazado" && amp.state !== "Cancelado");

                const ampliationsCount = validAmpliations.length;

                const workLinesCount = validAmpliations
                    .flatMap(amp => amp.workLines || [])
                    .length;

                return (
                    <div className='cursor' onClick={() => selectBudget(budget)}>
                        {`${ampliationsCount} (Líneas ${workLinesCount})`}
                    </div>
                );
            },
            selector: budget => {
                const validAmpliations = (budget.ampliations || [])
                    .filter(amp => amp.state !== "Rechazado" && amp.state !== "Cancelado");

                return validAmpliations.length;
            },
            sortable: true,
        },
        {
            name: t('admin.budgets.state'),
            grow: 1,
            center: true,
            cell: budget =>
                <div className='cursor' onClick={() => selectBudget(budget)}>
                    {budget.state}
                </div>,
            selector: budget => budget.state,
            sortable: true,
        },
        {
            name: t('global.actions'),
            grow: 0.5,
            center: true,
            cell: budget =>
                <div className="d-flex justify-content-center">
                    {userType === 'Admin' && budget.state === 'Pendiente' ?
                        <>
                            <div className='cursor mx-2' onClick={() => selectBudget(budget)}>
                                <i className='fas fa-pencil'></i>
                            </div>
                            <div className='cursor mx-2' onClick={(e) => handleDeleteBudget(e, budget._id)}>
                                <i className='fas fa-trash'></i>
                            </div>
                        </>
                        :
                        null
                    }
                    <div className='cursor mx-2' onClick={(e) => handleDuplicateBudget(e, budget._id)}>
                        <i class="fa-solid fa-copy"></i>
                    </div>
                </div>,
            sortable: false,
        }
    ]

    return (
        <Container className='mt-5 px-0'>
            <Row>
                <Col className='text-center'>
                    <h1>{t('admin.budgets.budgets')}</h1>
                </Col>
            </Row>
            <Row>
                {userType === 'Admin' &&
                    <Col xs={12} className='d-flex justify-content-end'>
                        <Button variant='primary' className="my-2 cursor" onClick={openModal}>
                            {t('admin.budgets.createbudget')}
                        </Button>
                    </Col>
                }
            </Row>
            {loading && !budgets
                ? <Loader />
                : <Container>
                    <Row className='justify-content-end'>
                        <Col xs={12} md={4} className='d-flex align-items-center'>
                            <input
                                type="text"
                                className="form-control me-2"
                                placeholder={t('datatables.buscar')}
                                value={searchAux || ''}
                                onChange={e => {
                                    setSearchAux(e.target.value)
                                    setPage(1)
                                }}
                                onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchAux); setPage(1) } }}
                            />
                            <Button onClick={() => setSearch(searchAux)}>
                                <i className='fas fa-search'></i>
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            {budgets?.length > 0
                                ? <CustomDataTable
                                    columns={columnsBudgets}
                                    data={budgets}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={total}
                                    paginationPerPage={limit}
                                    progressPending={loading}
                                    sortServer
                                    onSort={handleSort}
                                    defaultSortField={sortField}
                                    defaultSortAsc={sortOrder !== 'desc'}
                                    exportable={false}
                                    printable={false}
                                    onChangeRowsPerPage={newLimit => { setLimit(newLimit); setPage(1) }}
                                    onChangePage={newPage => setPage(newPage)}
                                />
                                : <h4 className='mt-4'>{t('admin.budgets.thereisnotbudgets')}</h4>
                            }
                        </Col>
                    </Row>
                </Container>
            }
            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                size="fullscreen"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <div style={{ display: "flex", gap: "1rem" }}>
                            <span
                                onClick={() => setActiveTab("form")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: activeTab === "form" ? "bold" : "normal",
                                    borderBottom: activeTab === "form" ? "2px solid #000" : "none"
                                }}
                            >
                                <h2>{t('admin.budgets.budgets')}</h2>
                            </span>
                            {formState.state !== 'Pendiente' &&
                                <span
                                    onClick={() => setActiveTab("ampliations")}
                                    style={{
                                        cursor: "pointer",
                                        fontWeight: activeTab === "ampliations" ? "bold" : "normal",
                                        borderBottom: activeTab === "ampliations" ? "2px solid #000" : "none"
                                    }}
                                >
                                    <h2>{t('admin.budgets.ampliations')}</h2>
                                </span>
                            }
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {activeTab === "form" && (
                        <BudgetForm
                            state={formState}
                            businessesActive={businessesActives}
                            handleInputChange={handleInputChange}
                            handleSelectChange={handleSelectChange}
                            handleDeleteBudgets={handleDeleteBudget}
                            submit={handleSubmit}
                            isAddingOrEditing={isAddingOrEditing}
                            isRegistering={isRegistering}
                            userType={userType}
                            handleConvertToWO={handleConvertToWO}
                        />
                    )}
                    {activeTab === "ampliations" && (
                        <AmpliationForm
                            state={formState}
                            ampliations={ampliations}
                            setAmpliations={setAmpliations}
                            onSubmitAmpliations={handleSubmitAmpliations}
                            isAddingOrEditing={isAddingOrEditing}
                            isRegistering={isRegistering}
                            userType={userType}
                            handleConvertToWO={handleConvertToWO}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    )
}